<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

interface Props {
  open: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  showClose?: boolean
}

withDefaults(defineProps<Props>(), {
  size: 'md',
  showClose: true
})

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'close'): void
}>()

const close = () => {
  emit('update:open', false)
  emit('close')
}

const sizeClasses = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
  full: 'sm:max-w-7xl'
}

// Repeated across DialogPanel/header/close-button/body below — kept in one place
// so the shade can't drift between them the way it has elsewhere in this folder.
const surfaceBg = 'bg-white dark:bg-gray-800'
</script>

<template>
  <!-- Teleport to body so overlay stacks above AppSidebar (fixed z-[9999]); modals inside main were trapped in main's z-0 context. -->
  <Teleport to="body">
    <TransitionRoot
      :show="open"
      as="template"
    >
      <Dialog
        as="div"
        class="relative z-[10050]"
        @close="close"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-900/70 transition-opacity"
            aria-hidden="true"
          />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95 motion-reduce:translate-y-0 motion-reduce:scale-100"
              enter-to="opacity-100 translate-y-0 sm:scale-100 motion-reduce:translate-y-0 motion-reduce:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100 motion-reduce:translate-y-0 motion-reduce:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95 motion-reduce:translate-y-0 motion-reduce:scale-100"
            >
              <DialogPanel
                :class="[
                  `relative flex max-h-[calc(100dvh-2rem)] w-full transform flex-col overflow-hidden rounded-xl ${surfaceBg} text-start shadow-2xl transition-all sm:my-8 sm:max-h-[calc(100dvh-4rem)]`,
                  sizeClasses[size]
                ]"
              >
                <!-- Header -->
                <div
                  v-if="title || $slots.header || showClose"
                  :class="`${surfaceBg} shrink-0 px-4 pt-5 pb-4 sm:p-6 border-b border-gray-200 dark:border-gray-700`"
                >
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <DialogTitle
                        v-if="title"
                        as="h3"
                        class="text-lg font-semibold leading-6 text-body"
                      >
                        {{ title }}
                      </DialogTitle>
                      <slot name="header" />
                    </div>
                    <button
                      v-if="showClose"
                      type="button"
                      :aria-label="$t('common.close') || 'Close'"
                      :class="`touch-target rounded-md ${surfaceBg} text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 active:bg-gray-100 dark:active:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`"
                      @click="close"
                    >
                      <span class="sr-only">{{ $t('common.close') || 'Close' }}</span>
                      <XMarkIcon
                        class="h-6 w-6"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>

                <!-- Body: the only scrolling region — `@container` lets nested tables
                     react to the dialog width instead of the viewport. -->
                <div :class="`${surfaceBg} @container min-h-0 flex-1 overflow-y-auto px-4 pb-4 sm:p-6`">
                  <slot />
                </div>

                <!-- Footer -->
                <div
                  v-if="$slots.footer"
                  class="shrink-0 bg-gray-50 dark:bg-gray-900/50 px-4 py-3 flex flex-col-reverse gap-3 sm:flex-row sm:flex-row-reverse sm:px-6 border-t border-gray-200 dark:border-gray-700"
                >
                  <slot name="footer" />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>
